import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import RowActions from "../components/RowActions";
import AcademicHierarchyHelper from "../components/AcademicHierarchyHelper";
import { useAuth } from "../contexts/AuthContext";
import { localName } from "../lib/localName";

const EMPTY_STAGE = { name: "", name_ar: "", order: 0 };
const EMPTY_GRADE = { stage_id: "", name: "", name_ar: "", order: 0 };

export default function GradesPage() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [schools, setSchools] = useState([]);
  const [stages, setStages] = useState([]);
  const [grades, setGrades] = useState([]);
  const [school, setSchool] = useState("");

  const [openStage, setOpenStage] = useState(false);
  const [editStage, setEditStage] = useState(null);
  const [stageForm, setStageForm] = useState(EMPTY_STAGE);

  const [openGrade, setOpenGrade] = useState(false);
  const [editGrade, setEditGrade] = useState(null);
  const [gradeForm, setGradeForm] = useState(EMPTY_GRADE);

  useEffect(() => {
    api.get("/schools").then(r => {
      const list = r.data || [];
      setSchools(list);
      setSchool(prev => {
        if (prev) return prev;
        if (!isSuper && user?.school_id && list.some(s => s.id === user.school_id)) return user.school_id;
        return list[0]?.id || "";
      });
    });
  }, [user?.school_id, isSuper]);

  const loadAll = () => {
    if (!school) return;
    api.get(`/stages?school_id=${school}`).then(r => setStages(r.data || []));
    api.get(`/grades?school_id=${school}`).then(r => setGrades(r.data || []));
  };
  useEffect(() => { loadAll(); }, [school]);

  // Stages
  const openNewStage = () => { setEditStage(null); setStageForm(EMPTY_STAGE); setOpenStage(true); };
  const openEditStage = (s) => { setEditStage(s); setStageForm({ name: s.name || "", name_ar: s.name_ar || "", order: s.order || 0 }); setOpenStage(true); };
  const saveStage = async () => {
    try {
      if (editStage) await api.put(`/stages/${editStage.id}`, stageForm);
      else await api.post("/stages", { ...stageForm, school_id: school });
      toast.success(t("success"));
      setOpenStage(false); setStageForm(EMPTY_STAGE); setEditStage(null);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const removeStage = async (s) => {
    try {
      await api.delete(`/stages/${s.id}`);
      toast.success(t("success"));
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  // Grades
  const openNewGrade = () => { setEditGrade(null); setGradeForm(EMPTY_GRADE); setOpenGrade(true); };
  const openEditGrade = (g) => { setEditGrade(g); setGradeForm({ stage_id: g.stage_id || "", name: g.name || "", name_ar: g.name_ar || "", order: g.order || 0 }); setOpenGrade(true); };
  const saveGrade = async () => {
    try {
      if (editGrade) await api.put(`/grades/${editGrade.id}`, gradeForm);
      else await api.post("/grades", { ...gradeForm, school_id: school });
      toast.success(t("success"));
      setOpenGrade(false); setGradeForm(EMPTY_GRADE); setEditGrade(null);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const removeGrade = async (g) => {
    try {
      await api.delete(`/grades/${g.id}`);
      toast.success(t("success"));
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6">
      <AcademicHierarchyHelper highlight="grade" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-semibold">{t("grades")}</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={school} onValueChange={setSchool} disabled={!isSuper}>
            <SelectTrigger className="w-56" data-testid="grades-school-select"><SelectValue placeholder={t("select_school")} /></SelectTrigger>
            <SelectContent>{schools.map(s => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}</SelectContent>
          </Select>

          <Dialog open={openStage} onOpenChange={(o) => { setOpenStage(o); if (!o) setEditStage(null); }}>
            <DialogTrigger asChild>
              <Button variant="outline" disabled={!school} onClick={openNewStage} data-testid="new-stage-btn">
                <Plus className="w-4 h-4 me-2" />{t("new_stage")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editStage ? t("edit_stage") : t("new_stage")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("nameAr")}</Label><Input value={stageForm.name_ar} onChange={e => setStageForm({ ...stageForm, name_ar: e.target.value })} data-testid="stage-name-ar" /></div>
                <div><Label>{t("nameEn")}</Label><Input value={stageForm.name} onChange={e => setStageForm({ ...stageForm, name: e.target.value })} data-testid="stage-name-en" /></div>
                <div><Label>Order</Label><Input type="number" value={stageForm.order} onChange={e => setStageForm({ ...stageForm, order: Number(e.target.value) })} /></div>
                <Button onClick={saveStage} className="w-full" data-testid="stage-save">{t("save")}</Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={openGrade} onOpenChange={(o) => { setOpenGrade(o); if (!o) setEditGrade(null); }}>
            <DialogTrigger asChild>
              <Button disabled={!school || stages.length === 0} onClick={openNewGrade} data-testid="new-grade-btn">
                <Plus className="w-4 h-4 me-2" />{t("new_grade")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editGrade ? t("edit_grade") : t("new_grade")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("stage")}</Label>
                  <Select value={gradeForm.stage_id} onValueChange={v => setGradeForm({ ...gradeForm, stage_id: v })}>
                    <SelectTrigger data-testid="grade-stage-select"><SelectValue placeholder={t("stage")} /></SelectTrigger>
                    <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{t("nameAr")}</Label><Input value={gradeForm.name_ar} onChange={e => setGradeForm({ ...gradeForm, name_ar: e.target.value })} data-testid="grade-name-ar" /></div>
                <div><Label>{t("nameEn")}</Label><Input value={gradeForm.name} onChange={e => setGradeForm({ ...gradeForm, name: e.target.value })} data-testid="grade-name-en" /></div>
                <div><Label>Order</Label><Input type="number" value={gradeForm.order} onChange={e => setGradeForm({ ...gradeForm, order: Number(e.target.value) })} /></div>
                <Button onClick={saveGrade} className="w-full" data-testid="grade-save">{t("save")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-5 border border-border shadow-none">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">{t("stages")}</div>
          <ul className="divide-y divide-border">
            {stages.map(s => (
              <li key={s.id} className="py-2 text-sm flex items-center justify-between gap-2" data-testid={`stage-row-${s.id}`}>
                <span>{localName(s, lang)}</span>
                <RowActions item={s} onEdit={openEditStage} onDelete={removeStage} testIdPrefix="stage" />
              </li>
            ))}
            {stages.length === 0 && <li className="text-sm text-muted-foreground py-3">{t("no_data")}</li>}
          </ul>
        </Card>

        <Card className="p-5 border border-border shadow-none">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">{t("grades")}</div>
          <ul className="divide-y divide-border">
            {grades.map(g => (
              <li key={g.id} className="py-2 text-sm flex items-center justify-between gap-2" data-testid={`grade-row-${g.id}`}>
                <div className="flex-1 flex justify-between gap-2">
                  <span>{localName(g, lang)}</span>
                  <span className="text-muted-foreground text-xs">{localName(stages.find(s => s.id === g.stage_id), lang)}</span>
                </div>
                <RowActions item={g} onEdit={openEditGrade} onDelete={removeGrade} testIdPrefix="grade" />
              </li>
            ))}
            {grades.length === 0 && <li className="text-sm text-muted-foreground py-3">{t("no_data")}</li>}
          </ul>
        </Card>
      </div>
    </div>
  );
}
